import {
  ContentDrawer,
  SearchIcon,
  AddIcon,
  Tooltip,
  AdminIcon,
  DeleteDangerIcon,
  // ChatAdmins,
  ConfirmDialog,
  UserItem,
  tools,
} from '@halo/ui-chat-react';
import { Stack, InputGroup, Center, Input, Box, Flex, Text } from '@chakra-ui/react';
import type { GroupMember } from '@halo/chat-sdk';
import { GroupMemberRole, StateEnum } from '@halo/chat-sdk';
import { memo, useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { MyTrans } from '@halo/ui-react';

interface GroupAdminsProps {
  title?: string;
  trigger?: React.ReactNode;
  members: GroupMember[];
  maxAdminCount: number;
  onSetAdminClick?: (member: GroupMember) => Promise<void>;
  onDeleteClick?: (member: GroupMember) => Promise<void>;
  onAddAdmin: () => void;
}

const GroupAdmins = ({
  title,
  trigger,
  members,
  maxAdminCount,
  onSetAdminClick,
  onDeleteClick,
  onAddAdmin,
}: GroupAdminsProps) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [operateMember, setOperateMember] = useState({} as GroupMember);
  const [detailMemberVisible, setDetailMemberVisible] = useState(false);
  // const [detailMember, setDetailMember] = useState({} as GroupMember);
  const [removeAdminLoading, setRemoveAdminLoading] = useState(false);
  const [setAdminLoading, setSetAdminLoading] = useState(false);

  const filteredMembers = useMemo(() => {
    return members?.filter((member) =>
      member.name?.toLowerCase().includes(search?.toLowerCase())
    );
  }, [members, search]);

  const adminAndOwner = (member: GroupMember) => {
    return member.role === GroupMemberRole.OWNER || member.role === GroupMemberRole.ADMIN;
  };

  const isAdmin = (member: GroupMember) => {
    return member.role === GroupMemberRole.ADMIN;
  };

  const { adminMembers, adminAndOwnerMembers } = useMemo(() => {
    const adminMembers = filteredMembers.filter((member) => isAdmin(member));
    const adminAndOwnerMembers = filteredMembers.filter((member) => adminAndOwner(member));
    return {
      adminMembers,
      adminAndOwnerMembers: adminAndOwnerMembers.sort((a, b) => {
        if (a.role === GroupMemberRole.OWNER) return -1;
        if (b.role === GroupMemberRole.OWNER) return 1;
        return 0;
      }),
    };
  }, [filteredMembers]);
  const normalMembers = useMemo(() => {
    return filteredMembers.filter(
      (member) =>
        member.role === GroupMemberRole.MEMBER || member.role === GroupMemberRole.STRANGER_INVITE
    );
  }, [filteredMembers]);

  const getRoleLabel = (role: GroupMemberRole) => {
    switch (role) {
      case GroupMemberRole.OWNER:
        return t('chat.groupRoleOwner');
      case GroupMemberRole.ADMIN:
        return t('chat.groupRoleAdmin');
      default:
        return '';
    }
  };

  const handleDeleteClick = useCallback((member: GroupMember) => {
    setOperateMember(member);
    setConfirm(true);
  }, []);

  const handleSetAdminClick = useCallback(
    async (member: GroupMember) => {
      if (adminMembers.length >= maxAdminCount) {
        tools.toaster.error({
          title: t('chatSettings.adminCountExceedsMaximumLimit'),
        });
        return;
      }
      if (setAdminLoading) return;
      setSetAdminLoading(true);
      await onSetAdminClick?.(member);
      setSetAdminLoading(false);
    },
    [adminMembers.length, maxAdminCount, setAdminLoading, onSetAdminClick, t]
  );
  // const handleDetailClick = useCallback((member: GroupMember) => {
  //   if (member.role !== GroupMemberRole.OWNER) {
  //     setDetailMember(member);
  //     setDetailMemberVisible(true);
  //   }
  // }, []);
  return (
    <ContentDrawer title={title} trigger={<div>{trigger}</div>}>
      <Stack h="full" gap="2">
        <InputGroup
          startElement={
            <Center flexShrink="0" fontSize="md">
              <SearchIcon />
            </Center>
          }
        >
          <Input
            size="sm"
            placeholder={t('filter.searchPlaceholder')}
            bg="bg"
            borderRadius="xl"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </InputGroup>
        <Stack gap="2">
          <Stack gap="0">
            <Box py="2.5" fontSize="xs" color="fg.muted" fontWeight="medium">
              {t('chatSettings.groupAdminsTitle')}
            </Box>
            <Box bg="bg" rounded="xl" py="1">
              <Flex px="2" alignItems="center" gap="2.5" cursor="pointer" onClick={onAddAdmin}>
                <Center fontSize="9xl">
                  <AddIcon />
                </Center>
                <Box flex="1" py="2.5" borderBottom="1px solid" borderColor="border.subtle">
                  <Text fontSize="sm" fontWeight="mediumBold">
                    {t('chatSettings.addAdmin')}
                  </Text>
                </Box>
              </Flex>
              <Box bg="bg" rounded="xl" px="1">
                {adminAndOwnerMembers.map((member, index) => (
                  <UserItem
                    key={member.user_id}
                    {...member}
                    rippleEffect={false}
                    hoverable={member.role === GroupMemberRole.OWNER ? true : false}
                    // onAvatarClick={() => handleDetailClick(member)}
                    userExtraElementRenderer={(isHovered) => (
                      <>
                        {isHovered && member.role !== GroupMemberRole.OWNER ? (
                          <Tooltip
                            content={t('chatSettings.dismissAdmin')}
                            positioning={{ placement: 'top' }}
                          >
                            <Center
                              fontSize="md"
                              color="fg.muted"
                              cursor="pointer"
                              onClick={() => handleDeleteClick(member)}
                            >
                              <DeleteDangerIcon />
                            </Center>
                          </Tooltip>
                        ) : adminAndOwner(member) ? (
                          <Center fontSize="xs" fontWeight="bold">
                            <Text color="fg.muted">{getRoleLabel(member.role)}</Text>
                          </Center>
                        ) : null}
                      </>
                    )}
                    borderColor={
                      index === adminAndOwnerMembers.length - 1 ? 'transparent' : 'border.subtle'
                    }
                  />
                ))}
              </Box>
            </Box>
          </Stack>
          {normalMembers.length > 0 && (
            <>
              <Stack gap="0">
                <Box py="2.5" fontSize="xs" color="fg.muted" fontWeight="medium">
                  {t('chat.groupMembersTitle')}
                </Box>
                <Box bg="bg" rounded="xl" p="1">
                  {normalMembers.map((member, index) => (
                    <UserItem
                      key={member.user_id}
                      {...member}
                      rippleEffect={false}
                      hoverable={true}
                      userExtraElementRenderer={(isHovered) => (
                        <>
                          {isHovered && member.state !== StateEnum.DELETE_ACCOUNT ? (
                            <Tooltip
                              content={t('chatSettings.setAsAdmin')}
                              positioning={{ placement: 'top' }}
                            >
                              <Center
                                w="24px"
                                h="24px"
                                fontSize="md"
                                color="fg.muted"
                                cursor={setAdminLoading ? 'not-allowed' : 'pointer'}
                                bg="bg.subtle"
                                onClick={() => {
                                  handleSetAdminClick(member);
                                }}
                              >
                                <AdminIcon />
                              </Center>
                            </Tooltip>
                          ) : null}
                        </>
                      )}
                      borderColor={
                        index === normalMembers.length - 1 ? 'transparent' : 'border.subtle'
                      }
                    />
                  ))}
                </Box>
              </Stack>
            </>
          )}
        </Stack>
      </Stack>
      <ConfirmDialog
        disabledPortal={false}
        open={confirm}
        onClose={() => setConfirm(false)}
        title={t('chatSettings.confirmDismissal')}
        subTitle={
          <MyTrans
            i18nKey="chatSettings.confirmDismissalSubTitle"
            components={{ component: <Text as="span" fontWeight="semibold" color="fg" /> }}
            values={{
              name: operateMember.name,
            }}
          />
        }
        onOk={async () => {
          setRemoveAdminLoading(true);
          await onDeleteClick?.(operateMember);
          setRemoveAdminLoading(false);
          if (detailMemberVisible) {
            setDetailMemberVisible(false);
          }
          setConfirm(false);
        }}
        okText={t('buttons.dismiss')}
        okButtonProps={{
          loading: removeAdminLoading,
        }}
      ></ConfirmDialog>

      {/* <ChatAdmins
        open={detailMemberVisible}
        onOpenChange={(open) => setDetailMemberVisible(open)}
        member={detailMember}
        dismissAdmin={() => handleDeleteClick(detailMember)}
        onAddCustomTitle={() => {}}
        onDeleteCustomTitle={() => {}}
      /> */}
    </ContentDrawer>
  );
};

const MemoizedGroupAdmins = memo(GroupAdmins);
export { MemoizedGroupAdmins as GroupAdmins };
